/**
 * Admin Feedback inbox (R7 follow-up). Lists all customer feedback rows
 * newest first. Bad ratings (<= 3) are highlighted so dispatchers can
 * follow up. Google-review clicks are tracked too so we can see who
 * chose the public path even without a private comment.
 */
import { BRAND } from '@fin/shared/brand';
import { Link } from 'react-router-dom';
import { useFeedback, type FeedbackEntry } from '../lib/queries';
import { Card, PageHeader } from '../components/ui';
import { PageIcon, PageAccent } from '../components/PageIcons';

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-AE', {
    timeZone: 'Asia/Dubai',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function isBadRating(f: FeedbackEntry): boolean {
  return f.source === 'private' && typeof f.rating === 'number' && f.rating <= 3;
}

function StarBar({ value }: { value?: number }) {
  if (typeof value !== 'number') return <span className="text-stone-400">—</span>;
  const stars = '★★★★★';
  return (
    <span style={{ color: BRAND.accent }} aria-label={`${value} stars`}>
      {stars.slice(0, value)}
      <span className="text-stone-300">{stars.slice(value)}</span>
    </span>
  );
}

export function FeedbackPage() {
  const { data, isLoading, error } = useFeedback();

  const rows = (data ?? []).slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const badCount = rows.filter(isBadRating).length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Customer feedback"
        subtitle="Reviews from your customers — public Google + private"
        icon={PageIcon.feedback}
        accent={PageAccent.feedback}
        action={
          badCount > 0 ? (
            <span
              className="rounded-full px-3 py-1 text-xs font-medium text-white shadow-sm"
              style={{ backgroundColor: BRAND.danger }}
            >
              {badCount} need follow-up
            </span>
          ) : undefined
        }
      />

      <Card>
        {isLoading ? (
          <p className="text-sm text-stone-500 p-3">Loading…</p>
        ) : error ? (
          <p className="text-sm text-red-600 p-3">{(error as Error).message}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-stone-500 p-3">No feedback yet.</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="text-left text-stone-500">
              <tr>
                <th className="py-2 px-3">When</th>
                <th className="py-2 px-3">Customer</th>
                <th className="py-2 px-3">Source</th>
                <th className="py-2 px-3">Rating</th>
                <th className="py-2 px-3">Comment</th>
                <th className="py-2 px-3">Contact</th>
                <th className="py-2 px-3">Job</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => {
                const flag = isBadRating(f);
                return (
                  <tr
                    key={`${f.jobId}#${f.source}`}
                    className="border-t border-stone-100"
                    style={flag ? { backgroundColor: '#FEF2F2' } : undefined}
                  >
                    <td className="py-2 px-3 whitespace-nowrap text-stone-600">{formatTimestamp(f.createdAt)}</td>
                    <td className="py-2 px-3">
                      {f.customerName ? (
                        <Link
                          to={`/jobs/${f.jobId}`}
                          className="hover:underline font-medium"
                          style={{ color: BRAND.primary }}
                          title={`Open the job this review was left for${f.customerPhone ? ` — ${f.customerPhone}` : ''}`}
                        >
                          {f.customerName}
                        </Link>
                      ) : (
                        <span className="text-stone-400">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className="rounded px-2 py-0.5 text-xs font-medium"
                        style={{
                          backgroundColor: f.source === 'google' ? '#DBEAFE' : '#F3F4F6',
                          color: f.source === 'google' ? '#1D4ED8' : '#374151',
                        }}
                      >
                        {f.source === 'google' ? 'Google' : 'Private'}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <StarBar value={f.rating} />
                    </td>
                    <td className="py-2 px-3 max-w-md text-stone-700">
                      {f.comment?.trim() || <span className="text-stone-400">—</span>}
                    </td>
                    <td className="py-2 px-3 text-stone-600">{f.contact || <span className="text-stone-400">—</span>}</td>
                    <td className="py-2 px-3 font-mono text-xs">
                      <Link
                        to={`/jobs/${f.jobId}`}
                        className="hover:underline"
                        style={{ color: BRAND.primary }}
                        title="Open job"
                      >
                        {f.jobId.slice(-8)}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  );
}
